import type { ReactNode } from 'react';
import { classNames } from '../lib/format';
import { EmptyState } from './ui';
import type { IconName } from './icons';

export interface Column<T> {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  emptyTitle = 'Kayıt yok',
  emptyHint,
  caption
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  emptyTitle?: string;
  emptyHint?: string;
  caption: string;
}) {
  if (rows.length === 0) {
    return <EmptyState icon="list" title={emptyTitle} hint={emptyHint} />;
  }

  return (
    <table className="table">
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.key} scope="col" style={{ width: column.width }} className={column.align === 'right' ? 'text-right' : undefined}>
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const key = rowKey(row);
          const clickable = Boolean(onRowClick);
          return (
            <tr
              key={key}
              aria-selected={selectedKey ? selectedKey === key : undefined}
              className={classNames(clickable ? 'cursor-pointer' : undefined)}
              onClick={clickable ? () => onRowClick?.(row) : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={
                clickable
                  ? (event) => {
                      if (event.key === 'Enter') onRowClick?.(row);
                    }
                  : undefined
              }
            >
              {columns.map((column) => (
                <td key={column.key} className={column.align === 'right' ? 'text-right' : undefined}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export type { IconName };
